import * as ngCore from '@angular/core'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../../../state'

@ngCore.Component({
  imports: [ds.layout.page.child.header.Component, ds.loader.Component],
  selector: 'atn-mosaic-page-header',
  standalone: true,
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.mosaic.endUser.currentRendering.Feature } */
  public readonly currentRendering = ngCore.inject(
    state.mosaic.endUser.currentRendering.Feature,
  )

  /** @see {@link state.variableOverrides.Feature} */
  private readonly _variablesFeature = ngCore.inject(
    state.variableOverrides.Feature,
  )

  /**
   * Returns true if the current page has variable definitions.
   */
  private readonly _hasPageVariablesSg = ngCore.computed(
    (context = this.currentRendering.selectors.context.asSignal()) =>
      context?.pageId
        ? this._variablesFeature.selectors
            .hasDefinitions({ page: { pageId: context.pageId } })
            .asSignal()
        : false,
  )

  /**
   * Returns true if the current app/session has variable definitions.
   */
  private readonly _hasSessionVariablesSg = ngCore.computed(
    (context = this.currentRendering.selectors.context.asSignal()) =>
      context?.appId
        ? this._variablesFeature.selectors
            .hasDefinitions({ session: { appId: context.appId } })
            .asSignal()
        : false,
  )

  /**
   * Returns true if the current page or session has variable definitions.
   */
  public readonly hasVariablesSg = ngCore.computed(
    (
      hasPageVariables = this._hasPageVariablesSg(),
      hasSessionVariables = this._hasSessionVariablesSg(),
    ) => hasPageVariables || hasSessionVariables,
  )

  /**
   * Returns true if app/session has applied variable values
   */
  private readonly _hasAppliedSessionVariableValuesSg = ngCore.computed(
    (context = this.currentRendering.selectors.context.asSignal()) =>
      context?.appId
        ? this._variablesFeature.selectors
            .hasAppliedValues({ session: { appId: context.appId } })
            .asSignal()
        : false,
  )

  /**
   * Returns true if page has applied variable values
   */
  private readonly _hasAppliedPageVariableValuesSg = ngCore.computed(
    (context = this.currentRendering.selectors.context.asSignal()) =>
      context?.pageId && context?.appId
        ? this._variablesFeature.selectors
            .hasAppliedValues({
              page: { appId: context.appId, pageId: context.pageId },
            })
            .asSignal()
        : false,
  )

  /**
   * Returns true if the current page or session has applied variable values.
   */
  public readonly hasAppliedVariableValuesSg = ngCore.computed(
    (
      hasAppliedPageVariableValues = this._hasAppliedPageVariableValuesSg(),
      hasAppliedSessionVariableValues = this._hasAppliedSessionVariableValuesSg(),
    ) => hasAppliedPageVariableValues || hasAppliedSessionVariableValues,
  )

  /**
   * Open variables slideout for rendered page.
   * If page variables exist, focus them. Otherwise, focus session variables.
   */
  private _openVariablesSlideoutHandler(): void {
    const context = this.currentRendering.selectors.context.asSignal()
    if (!context) {
      return
    }

    const { appId, pageId, scenarioId } = context

    if (this._hasPageVariablesSg()) {
      // Focus page variables
      this._variablesFeature.actions.setFocused
        .from('Mosaic.PageRenderer.FiltersBtn.Clicked')
        .dispatch({
          page: { appId, pageId, scenarioId },
        })
    } else if (this._hasSessionVariablesSg()) {
      // Focus session variables
      this._variablesFeature.actions.setFocused
        .from('Mosaic.PageRenderer.FiltersBtn.Clicked')
        .dispatch({
          session: { appId },
        })
    }
  }

  public filtersIconClickHandler(): void {
    this._openVariablesSlideoutHandler()
  }
}
